import { toast } from "sonner";
import { AccessButton } from "@acdc2/ui/components/access-button";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { useAuth } from "react-oidc-context";
import { useState } from "react";
import { useApolloClient } from "@apollo/client";
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@acdc2/ui/components/dialog";

type Props = {
  onDone?: () => void;
};

export function DocumentUploadDialogContent({ onDone }: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedFiles, setAcceptedFiles] = useState<File[]>([]);
  const { t } = useTranslation();
  const auth = useAuth();
  const apolloClient = useApolloClient();

  const { getRootProps, getInputProps, isDragAccept, isDragReject } =
    useDropzone({
      multiple: true,
      onDrop: setAcceptedFiles,
    });

  const uploadDocument = async (file: File) => {
    const data = new FormData();
    data.append("file", file);

    await fetch(import.meta.env.VITE_ACDC2_FILE_API_URL, {
      method: "POST",
      body: data,
      headers: { Authorization: `Bearer ${auth.user?.access_token}` },
    });
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const bodies = acceptedFiles.map((file) => uploadDocument(file));

    try {
      await Promise.allSettled(bodies);
    } catch (error) {
      toast(t("DocumentUploadDialogContent.error"), {
        description: new String(error),
      });
    }

    try {
      await apolloClient.refetchQueries({ include: "active" });
    } catch (error) {
      console.error("Failed to refetch queries", error);
    } finally {
      setIsLoading(false);
      setAcceptedFiles([]);
      onDone?.();
    }
  };

  return (
    <DialogContent showCloseButton={false}>
      <DialogHeader>
        <DialogTitle>{t("DocumentUploadDialogContent.title")}</DialogTitle>
        <DialogDescription>
          {t("DocumentUploadDialogContent.description")}
        </DialogDescription>
      </DialogHeader>
      <div
        {...getRootProps()}
        className="bg-gray-50 dark:bg-gray-900 border border-dashed cursor-pointer rounded min-h-32 flex justify-center items-center p-4"
        data-testid="documents-upload-dropzone"
      >
        <input {...getInputProps()} />
        {isDragAccept ? (
          <p>{t("DocumentUploadDialogContent.dropzone.accept")}</p>
        ) : isDragReject ? (
          <p>{t("DocumentUploadDialogContent.dropzone.reject")}</p>
        ) : acceptedFiles.length > 0 ? (
          <ol className="list-disc list-inside break-all">
            {acceptedFiles.map((file, index) => (
              <li key={index}>{file.name}</li>
            ))}
          </ol>
        ) : (
          <p>{t("DocumentUploadDialogContent.dropzone.select")}</p>
        )}
      </div>
      <DialogFooter>
        <AccessButton
          loading={isLoading}
          disabled={acceptedFiles.length === 0 || isLoading}
          onClick={onSubmit}
          children={t("DocumentUploadDialogContent.button")}
          data-testid="documents-upload-submit-button"
        />
      </DialogFooter>
    </DialogContent>
  );
}
